import { useEffect, useRef, useState } from "react";
import { sendChatMessage, ChatResponse } from "../../../api/chatApi";
import type { ChatMsg } from "../components/ChatPanel";
import type { Product } from "../../../types/product";
import { mapAiCardsToProducts } from "../../../types/productMapper";

export const useChatSearch = (initialText: string) => {
  const [sessionId, setSessionId] = useState<string>();
  const [messages, setMessages] = useState<ChatMsg[]>([
    { role: "user", content: initialText },
  ]);
  const [chips, setChips] = useState<string[]>([]);
  const [cards, setCards] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasShownFirstList, setHasShownFirstList] = useState(false);

  const [selectedChips, setSelectedChips] = useState<string[]>([]);

  const fetchedOnce = useRef(false);

  const pushUserOnce = (label: string) =>
    setMessages((prev) =>
      prev.at(-1)?.role === "user" && prev.at(-1)?.content === label
        ? prev
        : [...prev, { role: "user", content: label }]
    );

  const pushAssistantOnce = (text: string) =>
    setMessages((prev) => [...prev, { role: "assistant", content: text }]);

  const buildSearchWord = (base: string, conditions: string[]) => {
      return [base, ...conditions].filter(Boolean).join(" ");
  };

  // 初回ロード
  useEffect(() => {
    if (fetchedOnce.current) return;
    fetchedOnce.current = true;

    const fetchInitial = async () => {
      try {
        const res: ChatResponse = await sendChatMessage(initialText);
        setSessionId(res.session_id);

        if (res.chips?.length) setChips(res.chips);

        if (res.ask) pushAssistantOnce(res.ask);
        else if (res.summary) pushAssistantOnce(res.summary);

        if (res.cards && res.cards.length > 0) {
          setCards(mapAiCardsToProducts(res.cards));
        }
      } catch {
        pushAssistantOnce("初回通信に失敗しました。再読み込みしてお試しください。");
      } finally {
        setHasShownFirstList(true);
      }
    };

    fetchInitial();
  }, [initialText]);

  // ユーザー送信
  const handleUserMessage = async (userText: string) => {
    if (!userText.trim()) return;

    setIsLoading(true);
    setChips([]);
    pushUserOnce(userText);

    try {
      const res: ChatResponse = await sendChatMessage(
        userText,
        sessionId || undefined
      );
      setSessionId(res.session_id);
      setChips(res.chips || []);

      if (res.cards && res.cards.length > 0) {
        setCards(mapAiCardsToProducts(res.cards));

        // ★ 初回の商品リストが出た印
        setHasShownFirstList(true);
      }

      if (res.ask) pushAssistantOnce(res.ask);
      else if (res.summary) pushAssistantOnce(res.summary);

      if (res.cards && res.cards.length > 0) {
        setCards(mapAiCardsToProducts(res.cards));
      }
    } catch {
      pushAssistantOnce("通信エラーが発生しました。もう一度お試しください。");
    } finally {
      setIsLoading(false);  
    }
  };

  const handleChipSelect = async (chipText: string) => {
      const v = chipText.trim();
      if (!v) return;

      setIsLoading(true);
      setChips([]);

      // 会話ログにはクリックした条件を出す
      pushUserOnce(v);

      // 条件を蓄積（重複除外）
      const nextSelected = Array.from(new Set([...selectedChips, v]));
      setSelectedChips(nextSelected);

      // initialText（検索窓入力）＋ 選択条件 をまとめて検索ワード化
      const searchWord = buildSearchWord(initialText, nextSelected);

      try {
          const res: ChatResponse = await sendChatMessage(
              searchWord,
              sessionId || undefined
          );
          setSessionId(res.session_id);
          setChips(res.chips || []);

          if (res.ask) pushAssistantOnce(res.ask);
          else if (res.summary) pushAssistantOnce(res.summary);

          if (res.cards && res.cards.length > 0) {
              setCards(mapAiCardsToProducts(res.cards));
              setHasShownFirstList(true);
          }
      } catch {
          pushAssistantOnce("通信エラーが発生しました。もう一度お試しください。");
      } finally {
          setIsLoading(false);
      }
  };

  return {
    messages,
    chips,
    cards,
    isLoading,
    hasShownFirstList,
    selectedChips,
    handleUserMessage,
    handleChipSelect,
  };
};
